<template>
	<div>
		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('minimum_score') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('minimum_score_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-input
					v-model="minimum_score"
					ref="minimum_score"
					type="number"
					:error="$v.minimum_score.$error"
					@input="$v.minimum_score.$touch"
					:disable="has_answers"
				/>
				<span v-if="$v.minimum_score.$error" class="text-negative" style="display: block; margin-top: 5px;">{{ $t('minimum_score_error') }}</span>
			</div>

			<div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('score') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('score_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-input
					v-model="score"
					ref="score"
					type="number"
					:error="$v.score.$error"
					@input="$v.score.$touch"
					:disable="has_answers"
				/>
			</div>
			<div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('timezone') }}
				</p>
				<q-select
					class="q-mt-sm"
					v-model="timezone"
					:options="timezones"
					@input="changeTimezone"
					filter
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('date_type') }}
					<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('date_type_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="date_type"
					ref="date_type"
					:options="date_type_list"
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="date_type === 2">
				<p class="caption" style="margin: 0;">
						{{ $t('weekdays') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('weekdays_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="weekdays"
					ref="weekdays"
					multiple
					chips
					:options="weekdays_list"
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="date_type === 1 || date_type === 2">
				<p class="caption" style="margin: 0;">
						{{ $t('start_date') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('start_date_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-datetime
					type="date"
					format="DD/MM/YYYY"
					v-model="start_date"
					clearable
					:error="$v.start_date.$error"
					:disable="has_answers"
				/>
				<span v-if="$v.start_date.$error" class="text-negative" style="display: block; margin-top: 5px;">{{ $t('date_start_error') }}</span>
			</div>

			<div class="col-xs-12 col-sm-6 q-mb-sm" v-if="date_type === 1 || date_type === 2">
				<p class="caption" style="margin: 0;">
						{{ $t('end_date') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('end_date_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-datetime
					type="date"
					format="DD/MM/YYYY"
					v-model="end_date"
					clearable
					:error="$v.end_date.$error"
					:disable="has_answers"
				/>
				<span v-if="$v.end_date.$error" class="text-negative" style="display: block; margin-top: 5px;">{{ $t('date_start_error') }}</span>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="date_type === 3">
				<p class="caption" style="margin: 0;">
						{{ $t('days_of_the_month') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('days_of_the_month_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="days_of_the_month"
					ref="days_of_the_month"
					multiple
					clearable
					chips
					:options="days_of_the_month_list"
					:error="$v.days_of_the_month.$error"
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="date_type === 3">
				<p class="caption" style="margin: 0;">
						{{ $t('start_month') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('start_month_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="start_month"
					ref="start_month"
					:options="months_list"
					:error="$v.start_month.$error"
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="date_type === 3">
				<p class="caption" style="margin: 0;">
						{{ $t('end_month') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('end_month_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="end_month"
					ref="end_month"
					:options="months_list"
					:error="$v.end_month.$error"
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6" v-if="date_type === 4">
				<p class="caption" style="margin: 0;">
						{{ $t('custom_dates') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('custom_dates_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<date-picker 
					:error="$v.custom_dates.$error"
					:lang="calendar_lang"
					@input="$v.custom_dates.$touch"
					class="full-width"
					format="DD/MM/YYYY"
					id="date-by-month"
					multiple
					type="date"
					v-model="custom_dates" 
				></date-picker>
			</div>

			<div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('show_feedback') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('show_feedback_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="show_feedback"
					ref="show_feedback"
					:options="show_feedback_list"
					:disable="has_answers"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('allow_approved_to_answer') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('allow_approved_to_answer_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-select
					v-model="allow_approved_to_answer"
					ref="allow_approved_to_answer"
					:options="allow_approved_to_answer_list"
					:disable="has_answers"
				/>
			</div>

			<!-- <div class="col-xs-12 col-sm-6">
				<p class="caption" style="margin: 0;">
						{{ $t('time_to_answer') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('time_to_answer_info') }}
						</q-tooltip>
					</q-icon>
				</p>
				<q-datetime
					type="time"
					format24h
					format-model="string"
					v-model="time_to_answer"
					clearable
					:disable="has_answers"
				/>
			</div> -->
			<div class="col-12 row">
				<div class="col-xs-12 col-sm-6">
					<p class="caption" style="margin: 0;">
						{{ $t('qty_questions_frequency') }}
						<q-icon
						name="fal fa-question-circle"
						size="1.5rem"
						color="grey"
						class="q-ml-sm q-pb-sm"
					>
						<q-tooltip
							class="custom-tooltip"
							anchor="top left"
							self="bottom right"
						>
							{{ $t('qty_questions_frequency_info') }}
						</q-tooltip>
					</q-icon>
					</p>
					<q-input
						v-model="qty_questions_frequency"
						ref="qty_questions_frequency"
						type="number"
						:error="$v.qty_questions_frequency.$error"
						@input="$v.qty_questions_frequency.$touch"
						:disable="has_answers"
					/>
					<span v-if="$v.qty_questions_frequency.$error" class="text-negative" style="display: block; margin-top: 5px;">
						{{ $t('invalid_field') }}
						<q-icon
							name="fal fa-question-circle"
							size="1.5rem"
							color="red"
							class="q-ml-sm q-pb-sm"
						>
							<q-tooltip
								class="custom-tooltip"
								anchor="top left"
								self="bottom right"
							>
								{{ $t('qty_questions_frequency_error') }}
							</q-tooltip>
						</q-icon>
					</span>
				</div>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_drills' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3">
				<q-btn
					color="negative"
					class="full-width"
					:label="$t('unpublish')"
					icon="far fa-ban"
					@click="unpublish()"
				>
				</q-btn>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="positive"
					class="full-width"
					:disable="is_publish_disabled"
					:label="$t('publish')"
					icon="far fa-upload"
					@click="publish()"
				>
				</q-btn>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import { required, requiredIf, maxValue, minValue, maxLength } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
	i18nOptions: { namespaces: ['custom', 'drill', 'admin', 'common'] },
	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			drill_id: 0,
			areas: [],
			description: '',
			end_date: null,
			profiles: [],
			score: 0,
			start_date: null,
			title: '',
			user_groups: [],
			tab: 'permissions',
			status: 2,
			statuses: [
				{ label: this.$t('published'), value: 1 },
				{ label: this.$t('unpublished'), value: 2 },
			],
			all_profiles: false,
			selected_client_params: [],
			all_areas: false,
			selected_country: null,
			image_url: '',
			minimum_score: 1,
			is_mandatory: false,
			weekdays: [],
			weekdays_list: [
				{
					label: this.$t('monday'),
					value: 1
				},
				{
					label: this.$t('tuesday'),
					value: 2
				},
				{
					label: this.$t('wednesday'),
					value: 3
				},
				{
					label: this.$t('thursday'),
					value: 4
				},
				{
					label: this.$t('friday'),
					value: 5
				},
				{
					label: this.$t('saturday'),
					value: 6
				},
				{
					label: this.$t('sunday'),
					value: 7
				},
			],
			date_type: 1,
			date_type_list: [
				{
					label: this.$t('date_type_range'),
					value: 1
				},
				{
					label: this.$t('date_type_weekdays'),
					value: 2
				},
				{
					label: this.$t('date_type_month'),
					value: 3
				},
				{
					label: this.$t('date_type_custom'),
					value: 4
				},
			],
			show_feedback_list: [
				{
					label: this.$t('yes'),
					value: true
				},
				{
					label: this.$t('no'),
					value: false
				}
			],
			allow_approved_to_answer_list: [
				{
					label: this.$t('yes'),
					value: true
				},
				{
					label: this.$t('no'),
					value: false
				}
			],
			has_answers_list: [
				{
					label: this.$t('yes'),
					value: true
				},
				{
					label: this.$t('no'),
					value: false
				}
			],
			qty_questions_frequency: 1,
			time_to_answer: null,
			drill_qty_questions: 0,
			show_feedback: true,
			allow_approved_to_answer: false,
			has_answers: false,
			custom_dates: [],
			days_of_the_month: [],
			timezone: '',
			days_of_the_month_list: [	
				{
					label: '1',
					value: 1
				},
				{
					label: '2',
					value: 2
				},
				{
					label: '3',
					value: 3
				},
				{
					label: '4',
					value: 4
				},
				{
					label: '5',
					value: 5
				},
				{
					label: '6',
					value: 6
				},
				{
					label: '7',
					value: 7
				},
				{
					label: '8',
					value: 8
				},
				{
					label: '9',
					value: 9
				},
				{
					label: '10',
					value: 10
				},
				{
					label: '11',
					value: 11
				},
				{
					label: '12',
					value: 12
				},
				{
					label: '13',
					value: 13
				},
				{
					label: '14',
					value: 14
				},
				{
					label: '15',
					value: 15
				},
				{
					label: '16',
					value: 16
				},
				{
					label: '17',
					value: 17
				},
				{
					label: '18',
					value: 18
				},
				{
					label: '19',
					value: 19
				},
				{
					label: '20',
					value: 20
				},
				{
					label: '21',
					value: 21
				},
				{
					label: '22',
					value: 22
				},
				{
					label: '23',
					value: 23
				},
				{
					label: '24',
					value: 24
				},
				{
					label: '25',
					value: 25
				},
				{
					label: '26',
					value: 26
				},
				{
					label: '27',
					value: 27
				},
				{
					label: '28',
					value: 28
				},
				{
					label: '29',
					value: 29
				},
				{
					label: '30',
					value: 30
				},
				{
					label: '31',
					value: 31
				}
			],
			start_month: null,
			end_month: null,
			months_list: [
				{
					label: this.$t('january'),
					value: 1
				},
				{
					label: this.$t('february'),
					value: 2
				},
				{
					label: this.$t('march'),
					value: 3
				},
				{
					label: this.$t('april'),
					value: 4
				},
				{
					label: this.$t('may'),
					value: 5
				},
				{
					label: this.$t('june'),
					value: 6
				},
				{
					label: this.$t('july'),
					value: 7
				},
				{
					label: this.$t('august'),
					value: 8
				},
				{
					label: this.$t('september'),
					value: 9
				},
				{
					label: this.$t('october'),
					value: 10
				},
				{
					label: this.$t('november'),
					value: 11
				},
				{
					label: this.$t('december'),
					value: 12
				},
			]
		}
	},

	components: {
		uploader: Uploader,
		'date-picker': DatePicker
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			id_user_client_country_selected: 'common/id_user_client_country_selected',
			areas_list: 'registration_lists/country_areas',
			user_groups_list: 'registration_lists/group_users',
			profiles_list: 'registration_lists/profiles',
			user_id: 'common/id',
			timezones: 'registration_lists/timezones',
			tags: 'registration_lists/tags',
			user_full_name: 'common/full_name',
			user_email: 'common/email',
			languages: 'params/languages',
			countries: 'registration_lists/countries',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getColorStatus() {
			if (this.status == 1) return 'positive'
			else if (this.status == 2) return 'negative'
			else return 'warning'
		},

		calendar_lang() {
			const lang = {
				days: [
					this.$t('sunday').slice(0, 3),
					this.$t('monday').slice(0, 3),
					this.$t('tuesday').slice(0, 3),
					this.$t('wednesday').slice(0, 3),
					this.$t('thursday').slice(0, 3),
					this.$t('friday').slice(0, 3),
					this.$t('saturday').slice(0, 3)
				],
				months: [this.$t('january'), this.$t('february'), this.$t('march'), this.$t('april'), this.$t('may'), this.$t('june'),
					this.$t('july'), this.$t('august'), this.$t('september'), this.$t('october'), this.$t('november'), this.$t('december')],
				monthsShort: [
					this.$t('january').slice(0, 3),
					this.$t('february').slice(0, 3),
					this.$t('march').slice(0, 3),
					this.$t('april').slice(0, 3),
					this.$t('may').slice(0, 3),
					this.$t('june').slice(0, 3),
					this.$t('july').slice(0, 3),
					this.$t('august').slice(0, 3),
					this.$t('september').slice(0, 3),
					this.$t('october').slice(0, 3),
					this.$t('november').slice(0, 3),
					this.$t('december').slice(0, 3)
				]
			};

			return lang
		},
		
		drill_dates_by_type() {
			switch (this.date_type) {
				case 1:
					return this.createListDate(this.start_date, this.end_date)
					break;
				case 2:
					return this.getAllDaysInWeekdays(this.start_date,this.end_date)
					break;
				case 3:
					return this.createListDateByMonth(this.start_month,this.end_month,this.days_of_the_month)
					break;
				case 4:
					return this.sortDatesSmallToLarge(this.custom_dates)
					break;			
				default:
					return []
					break;
			}
		},

		is_publish_disabled() {
			if (this.date_type == 2) {
				return this.weekdays.length == 0 && !this.start_date && !this.end_date
			}
			if (this.date_type == 3) {
				return this.days_of_the_month.length == 0 && !this.start_date && !this.end_date
			}
			if (this.date_type == 4) {
				return this.custom_dates.length == 0
			}
			return !this.start_date && !this.end_date 
		}
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_drills/drills/${this.drill_id}/`, this.getHeader)
				.then((response) => {
					this.areas = response.data.areas
					this.description = response.data.description
					this.start_date = response.data.drill_dates[0]
						? moment(response.data.drill_dates[0])
								.tz('Etc/GMT')
								.format('YYYY-MM-DDTHH:mm:ss.SSS')
						: null
					this.end_date = response.data.drill_dates[response.data.drill_dates.length - 1]
						? moment(response.data.drill_dates[response.data.drill_dates.length - 1])
								.tz('Etc/GMT')
								.format('YYYY-MM-DDTHH:mm:ss.SSS')
						: null
					this.start_month = new Date(response.data.drill_dates[0]).getMonth()+1
					this.weekdays = this.getWeekdays(response.data.drill_dates)
					this.end_month = new Date(response.data.drill_dates[response.data.drill_dates.length - 1]).getMonth()+1
					this.days_of_the_month = this.getDaysOfTheMonth(response.data.drill_dates)
					this.custom_dates = response.data.drill_dates.map(dt => moment(dt, "YYYY-MM-DDTHH:mm:ss.SSS").tz(response.data.timezone).toDate())
					this.timezone = response.data.timezone
					this.profiles = response.data.profiles
					this.title = response.data.title
					this.user_groups = response.data.user_groups
					this.score = response.data.score
					this.minimum_score = response.data.minimum_score
					this.is_mandatory = response.data.is_mandatory
					this.status = response.data.status
					this.qty_questions_frequency = response.data.qty_questions_frequency
					this.image_url = response.data.drill_images.length ? response.data.drill_images[0].image : ''
					this.drill_qty_questions = response.data.drill_qty_questions
					this.time_to_answer = date.buildDate({
								hours: this.getSecondsToTime(response.data.time_to_answer).split(':')[0],
								minutes: this.getSecondsToTime(response.data.time_to_answer).split(':')[1],
							})
					this.language_br = response.data.drill_image_alt_text.length ? response.data.drill_image_alt_text.find(alt => alt.language == 'pt-br').alt_text : ''
					this.language_us = response.data.drill_image_alt_text.length ? response.data.drill_image_alt_text.find(alt => alt.language == 'en-us').alt_text : ''
					this.language_es = response.data.drill_image_alt_text.length ? response.data.drill_image_alt_text.find(alt => alt.language == 'es-es').alt_text : ''
					this.language_fr = response.data.drill_image_alt_text.length ? response.data.drill_image_alt_text.find(alt => alt.language == 'fr-fr').alt_text : ''
					this.show_feedback = response.data.show_feedback
					this.allow_approved_to_answer = response.data.allow_approved_to_answer
					this.has_answers = response.data.has_answers
					this.date_type = response.data.date_type
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const data = {
					title: this.title,
					description: this.description,
					drill_dates: this.drill_dates_by_type.map(dt => 
						moment(dt, dt.includes('/') ? 'DD/MM/YYYY' : 'YYYY-MM-DDTHH:mm:ss.SSS')
							.format('YYYY-MM-DDTHH:mm:ss.SSS')
					),
					areas: this.areas,
					profiles: this.profiles,
					user_groups: this.user_groups,
					country: this.selected_country.value,
					client: this.selected_client_params.id,
					is_mandatory: this.is_mandatory,
					status: this.status,
					drill_images: this.image_url,
					score: this.score,
					minimum_score: this.minimum_score,
					qty_questions_frequency: this.qty_questions_frequency,
					time_to_answer: this.time_to_answer ? this.getTimeToSeconds(this.time_to_answer) : null,
					drill_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
					timezone: this.timezone,
					date_type: this.date_type,
					show_feedback: this.show_feedback,
					allow_approved_to_answer: this.allow_approved_to_answer,
				}
				

				if (this.drill_id != 0) {
					this.$axios
						.put(`/admin_drills/drills/${this.drill_id}/`, data, this.getHeader)
						.then((response) => {
							this.$q.notify({
								message: this.$t('drill_save_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('drill_save_error'),
								type: 'negative',
								icon: 'fal fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				} else {
					this.$axios
						.post(`/admin_drills/drills/`, data, this.getHeader)
						.then((response) => {
							this.drill_id = response.data.id
							this.$router.push({
								name: 'admin_drill',
								params: { id: response.data.id },
							})

							this.$q.notify({
								message: this.$t('drill_save_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.toastError('drill_save_error')

						})
						.finally(() => {
							this.$q.loading.hide()
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				if (this.$v.score.maxValue) {
					this.toastError('make_sure_that_all_fields_are_filled_in')
				}
			}
		},

		publish() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()
				
				let fd = new FormData()

				this.$axios
					.post(`/admin_drills/drills/${this.drill_id}/publish/`, fd, this.getHeader)
					.then((response) => {
						this.$q.loading.hide()
						this.$router.push({ name: 'admin_drills' })
					})
					.catch((e) => {
						console.log(e)
						this.toastError('drill_save_error')
					})
					.finally(() => {
						this.$q.loading.hide()
					})
			}
		},

		unpublish() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()
				
				let fd = new FormData()

				this.$axios
					.post(`/admin_drills/drills/${this.drill_id}/unpublish/`, fd, this.getHeader)
					.then((response) => {
						this.$q.loading.hide()
						this.$router.push({ name: 'admin_drills' })
					})
					.catch((e) => {
						console.log(e)
						this.toastError('drill_save_error')
					})
					.finally(() => {
						this.$q.loading.hide()
					})
			}
		},

		toastError(value) {
			this.$q.notify({
				message: this.$t(value),
				type: 'negative',
				icon: 'fal fa-exclamation-triangle',
				position: 'top',
			})
		},

		fetchTimezones() {
			let values = []

			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})

					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')

			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},

		changeTimezone(timezone) {
			this.timezone = timezone
		},

		getAllDaysInWeekdays(startDate, endDate) {
			var start = new Date(startDate);
			var end = new Date(endDate);
			var days = [];
			while (start <= end) {
				if (this.weekdays.includes(start.getDay())) {
					days.push(moment(new Date(start), 'YYYY-MM-DD').tz(this.timezone, true).format('YYYY-MM-DDTHH:mm:ssZ'));
				}
				start.setDate(start.getDate() + 1);
			}
					
			return days;
		},

		getDaysOfTheMonth(dateList) {
			let dtList = []
			dateList.forEach(dt => {
				dtList.push(moment(new Date(dt).getDate()+1).tz(this.timezone))
			});
			return [...new Set(dtList)]
		},

		getWeekdays(dateList) {
			let dtList = []
			dateList.forEach(dt => {
				dtList.push(new Date(dt).getDay()+1)
			});
			return [...new Set(dtList)]
		},

		sortDatesSmallToLarge(dates) {
			const datesSorts = dates.sort(function(a, b){
				let dataA = new Date(a), dataB = new Date(b);
				return dataA - dataB;
			});

			return datesSorts.map(d => date.formatDate(new Date(d), 'DD/MM/YYYY'))
		},

		getTimeToSeconds(time) {
			let dateTime = new Date(time)

			let hours = dateTime.getHours()
			let minutes = dateTime.getMinutes()

			return hours * 3600 + minutes * 60
		},

		getSecondsToTime(seconds) {
			let hours = Math.floor(seconds / 3600)
			let minutes = Math.floor((seconds % 3600) / 60)

			return `${hours}:${minutes}`
		},

		createListDate(start, end) {
			let dateList = [moment(start, 'YYYY-MM-DD').tz(this.timezone, true).format('YYYY-MM-DDTHH:mm:ssZ')]
			let newDate
			let endDate = new Date(date.subtractFromDate(end, { days: 1 }))
			if (start == end) return dateList

			while (new Date(dateList[dateList.length-1]) < endDate) {
				newDate = date.addToDate(new Date(dateList[dateList.length-1]), { days: 1 })
				dateList.push(moment(newDate, 'YYYY-MM-DD').tz(this.timezone, true).format('YYYY-MM-DDTHH:mm:ssZ'))
			}
			return dateList
		},

		createListDateByMonth(startMonth, endMonth, days) {
			const currentDate = new Date();
			const currentYear = currentDate.getFullYear();
			const dates_list = [];
			for (let month = startMonth; month <= endMonth; month++) {
				const lastDayOfMonth = new Date(currentYear, month, 0).getDate();
				const limitDay = month === endMonth ? Math.min(lastDayOfMonth, Math.max(...days)) : lastDayOfMonth;
				for (const day of days) {
					if (day <= limitDay) {
						const dt = new Date(currentYear, month - 1, day);
						dates_list.push(moment(dt.toISOString().slice(0, 10)).tz(this.timezone, true).format('YYYY-MM-DDTHH:mm:ssZ'));
					} else {
						const dt = new Date(currentYear, month - 1, limitDay);
						dates_list.push(moment(dt.toISOString().slice(0, 10)).tz(this.timezone, true).format('YYYY-MM-DDTHH:mm:ssZ'));
					}
				}
			}
			return dates_list;
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		imageUploaded(url) {
			this.image_url = url
		},
	},

	created() {
		this.fetchTimezones()

		this.drill_id = this.$route.params.id
		if (this.drill_id != 0) {
			this.fetchData()
		} else {
			this.timezone = moment.tz.guess()
			this.changeTimezone(this.timezone)
		}

		let client = this.clients.find((obj) => {
			return obj.id === this.id_user_client_selected
		})
		this.selected_client_params = client ? client : {}

		this.selected_country = this.countries.find(c => c.value == this.id_user_client_country_selected)
	},

	validations() {
		return {
			custom_dates: {
				requiredIf: requiredIf(() => {
					return this.date_type === 4
				}),
			},
			days_of_the_month: {
				requiredIf: requiredIf(() => {
					return this.date_type === 3
				}),
			},
			start_date: { 
				requiredIf: requiredIf(() => {
					return this.date_type !== 4
				}),
				minValue: () => {
					if (this.date_type !== 4) return 9999999
					return new Date(this.end_date) >= new Date(this.start_date)
				}
			},
			end_date: { 
				requiredIf: requiredIf(() => {
					return this.date_type !== 4
				}),
				minValue: () => {
					if (this.date_type !== 4) return 9999999
					return new Date(this.end_date) >= new Date(this.start_date)
				}
			},
			start_month: { 
				requiredIf: requiredIf(() => {
					return this.date_type !== 3
				}),
				minValue: () => {
					if (this.date_type !== 3) return 9999999
					return this.end_month >= this.start_month
				}
			},
			end_month: { 
				requiredIf: requiredIf(() => {
					return this.date_type !== 3
				}),
				minValue: () => {
					if (this.date_type !== 3) return 9999999
					return  this.start_month >= this.start_month
				}
			},
			score: {
				maxValue: maxValue(99999),
				minValue: minValue(0),
				required
			},
			minimum_score: {
				required,
				maxValue: maxValue(this.drill_qty_questions),
				minValue: minValue(1),
			},
			weekdays: {
				requiredIf: requiredIf(() => {
					return this.date_type === 2
				}),
			},
			qty_questions_frequency: {
				required,
				maxValue: (() => {
					if (this.date_type === 3 && this.sortDatesSmallToLarge(this.custom_dates).length < this.drill_qty_questions) {
						return this.qty_questions_frequency <= this.drill_qty_questions / this.sortDatesSmallToLarge(this.custom_dates).length
					}
					if (this.date_type === 2 && this.getAllDaysInWeekdays(this.start_date,this.end_date).length < this.drill_qty_questions) {
						return this.qty_questions_frequency <= this.drill_qty_questions / this.getAllDaysInWeekdays(this.start_date,this.end_date).length
					}
					if (this.createListDate(this.start_date, this.end_date).length < this.drill_qty_questions) {
						return this.qty_questions_frequency <= (this.drill_qty_questions / this.createListDate(this.start_date, this.end_date).length)
					}
					if (this.qty_questions_frequency > this.drill_qty_questions) return false
					return true
				}),
				minValue: (() => {
					if (this.qty_questions_frequency <= 0) return false
					if (this.date_type === 4) {
						if (this.sortDatesSmallToLarge(this.custom_dates).length < this.minimum_score) return this.qty_questions_frequency >= this.minimum_score
						return true
					}
					if (this.date_type === 2) {
						if (this.getAllDaysInWeekdays(this.start_date,this.end_date).length < this.minimum_score) return this.qty_questions_frequency >= this.minimum_score
						return true
					}
					if (this.createListDate(this.start_date, this.end_date).length < this.minimum_score) return this.qty_questions_frequency >= this.minimum_score
					return true
				}),
			},
		}
	},
}
</script>

<style>
.mx-input {
    border: none !important;
	border-bottom: 1px solid #979797 !important;
	border-radius: 0 !important;
	box-shadow: none !important;
}
</style>
